<template>
  <div class="chart-grid chart-grid-3">
    <div class="chart-card">
      <h3 class="card-title">Overall Health</h3>
      <div v-if="!hasOverallData" class="no-data">No Records Found</div>
      <div v-else class="chart-body">
          <apexchart
            type="donut"
            height="300"
            :options="overallChartOptions"
            :series="overallSeries"
          />
      </div>
    </div>
    <div class="chart-card">
      <h3 class="card-title">Health by Team</h3>
      <div v-if="!hasTeamData" class="no-data">No Records Found</div>
      <div v-else class="chart-body">
          <apexchart
            type="bar"
            height="300"
            :options="teamChartOptions"
            :series="teamSeries"
          />
      </div>
    </div>
    <div class="chart-card">
      <h3 class="card-title">Health by Location</h3>
      <div v-if="!hasLocationData" class="no-data">No Records Found</div>
      <div v-else class="chart-body">
          <apexchart
            type="bar"
            height="300"
            :options="locationChartOptions"
            :series="locationSeries"
          />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import {
  CONTROL_HEALTH_ORDER,
  countControlHealth,
  flattenControlHealth,
  groupControlHealthBy,
  stackedStatusSeries,
} from "../../utils/dashboardChartData";
import { donutOptions, stackedBarOptions } from "../../utils/dashboardTheme";

export default defineComponent({
  name: "ControlsSection",
  props: {
    controlTeams: { type: Array, default: () => [] },
  },
  data(): any {
    return {};
  },
  computed: {
    controlHealthRows(): Array<{
      locationId: string;
      locationName: string;
      teamId: string;
      teamName: string;
      controlHealth: number | null;
      label: string;
    }> {
      return flattenControlHealth(this.controlTeams as any[]);
    },
    overallCounts(): { label: string; total: number }[] {
      return countControlHealth(this.controlHealthRows);
    },
    hasOverallData(): boolean {
      return this.overallCounts.length > 0;
    },
    overallKey(): string {
      const counts: { label: string; total: number }[] = this.overallCounts;
      return counts.map((item: { label: string; total: number }) => `${item.label}-${item.total}`).join("-");
    },
    overallSeries(): number[] {
      const counts: { label: string; total: number }[] = this.overallCounts;
      return counts.map((item: { total: number }) => item.total);
    },
    overallChartOptions(): Record<string, any> {
      const counts: { label: string; total: number }[] = this.overallCounts;
      return donutOptions(counts.map((item: { label: string }) => item.label));
    },
    teamRows(): Array<{ id: string; name: string; status: Array<{ label: string; total: number }> }> {
      return groupControlHealthBy(this.controlHealthRows, "teamId", "teamName");
    },
    hasTeamData(): boolean {
      return this.teamRows.length > 0;
    },
    teamKey(): string {
      const teams: Array<{ name: string }> = this.teamRows;
      return teams.map((team: { name: string }) => team.name).join("-");
    },
    teamSeries(): { name: string; data: number[] }[] {
      return stackedStatusSeries(
        this.teamRows,
        CONTROL_HEALTH_ORDER.map((entry: { label: string }) => entry.label)
      );
    },
    teamChartOptions(): Record<string, any> {
      const teams: Array<{ name: string }> = this.teamRows;
      return stackedBarOptions(
        teams.map((team: { name: string }) => team.name),
        this.teamSeries.map((series: { name: string }) => series.name)
      );
    },
    locationRows(): Array<{ id: string; name: string; status: Array<{ label: string; total: number }> }> {
      return groupControlHealthBy(this.controlHealthRows, "locationId", "locationName");
    },
    hasLocationData(): boolean {
      return this.locationRows.length > 0;
    },
    locationKey(): string {
      const locations: Array<{ name: string }> = this.locationRows;
      return locations.map((location: { name: string }) => location.name).join("-");
    },
    locationSeries(): { name: string; data: number[] }[] {
      return stackedStatusSeries(
        this.locationRows,
        CONTROL_HEALTH_ORDER.map((entry: { label: string }) => entry.label)
      );
    },
    locationChartOptions(): Record<string, any> {
      const locations: Array<{ name: string }> = this.locationRows;
      return stackedBarOptions(
        locations.map((location: { name: string }) => location.name),
        this.locationSeries.map((series: { name: string }) => series.name)
      );
    },
  },
});
</script>
